"use client";

import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
	type SyntheticEvent,
} from "react";

import { CART_OPEN_TRIGGERS, type CartOpenTrigger } from "@/lib/analytics/checkout-diagnostic-events";
import {
	appendPendingEntry,
	createPendingEntry,
	dropFailedPendingEntries,
	reconcilePendingEntries,
	replacePendingEntry,
	settlePendingEntry,
	type AddToCartRequest,
	type AuthoritativeCartLine,
	type PendingCartEntry,
	type PendingCartLineSettlement,
} from "./pending-cart-lines";

/**
 * Outcome of a retry request:
 * - `replaced`  — the failed row was swapped for a fresh attempt (normal retry)
 * - `restarted` — the row had been dismissed with the drawer, so the retry starts a new row
 * - `blocked`   — nothing to retry (already retried, still in flight, or unknown id).
 *   The runner MUST abort on this: it is the double-click guard.
 */
type ReplacePendingCartLineResult = "replaced" | "restarted" | "blocked";

export type MutationExpectation = { lineId: string; quantity: number | null };
type CartSnapshotLine = { id: string; quantity: number };

interface CartContextType {
	mutationLocked: boolean;
	upgradePending: boolean;
	acquireMutation: () => boolean;
	releaseMutation: () => void;
	awaitMutationSnapshot: (expected: MutationExpectation) => void;
	unresolvedMutation: MutationExpectation | null;
	retainUnknownMutation: (expected: MutationExpectation) => void;
	retryUnknownMutation: (expected: MutationExpectation) => boolean;
	syncMutationSnapshot: (lines: CartSnapshotLine[]) => void;
	/** The latest synced snapshot, read by `cart_line_changed` only; null before the first sync. */
	readMutationSnapshot: () => readonly CartSnapshotLine[] | null;
	setUpgradePending: (pending: boolean) => void;
	isMutationBlocked: () => boolean;
	isOpen: boolean;
	/**
	 * Opens the drawer. The optional trigger only feeds `cart_opened`; a click handler
	 * wired as `onClick={openCart}` passes its event, which counts as `other`.
	 */
	openCart: (trigger?: CartOpenTrigger | SyntheticEvent) => void;
	closeCart: () => void;
	toggleCart: () => void;
	/** The trigger of the latest opening, for `cart_opened`; reading it resets it to `other`. */
	consumeOpenTrigger: () => CartOpenTrigger;
	/**
	 * Transient rows for adds the authoritative server props have not caught up with.
	 * Rendered by the drawer; written exclusively through the `useAddToCart` runner.
	 */
	pendingLines: PendingCartEntry[];
	startPendingCartLine: (request: AddToCartRequest, attemptId: string) => void;
	replacePendingCartLine: (
		previousAttemptId: string,
		request: AddToCartRequest,
		attemptId: string,
	) => ReplacePendingCartLineResult;
	settlePendingCartLine: (attemptId: string, settlement: PendingCartLineSettlement) => void;
	/**
	 * Publishes the authoritative Saleor lines (drawer props). This — and nothing
	 * else, no timer — retires confirmed transient rows.
	 */
	syncAuthoritativeCartLines: (lines: AuthoritativeCartLine[]) => void;
	/** Saleor checkout id learned from an add response, for surfaces rendered before
	 *  any server props carry one. A scalar identifier only — never money.
	 *  Carries its channel: a checkout belongs to exactly one. */
	noteCheckoutId: (channel: string, checkoutId: string) => void;
	knownCheckout: { channel: string; checkoutId: string } | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
	const [isOpen, setIsOpen] = useState(false);
	const [mutationLocked, setMutationLocked] = useState(false);
	const [upgradePending, commitUpgradePending] = useState(false);
	const mutationRef = useRef(false);
	const upgradeRef = useRef(false);
	const expectedRef = useRef<MutationExpectation | null>(null);
	const unresolvedRef = useRef<MutationExpectation | null>(null);
	const retryInFlightRef = useRef(false);
	const [unresolvedMutation, setUnresolvedMutation] = useState<MutationExpectation | null>(null);
	const snapshotRef = useRef<CartSnapshotLine[] | null>(null);
	const [pendingLines, setPendingLines] = useState<PendingCartEntry[]>([]);
	/**
	 * The transient rows are also kept in a ref and every mutation reads THAT, not the
	 * render snapshot: two clicks in the same tick must see each other's effect before
	 * React has re-rendered, otherwise a double-clicked retry posts the line twice.
	 */
	const entriesRef = useRef<PendingCartEntry[]>([]);
	/** Latest authoritative snapshot, so a new attempt can size its expectation. */
	const authoritativeRef = useRef<AuthoritativeCartLine[]>([]);
	/** Failed rows dismissed by closing the drawer — their toast retry still works. */
	const dismissedRef = useRef(new Set<string>());

	/** Single write path. Same array back = nothing changed = no re-render. */
	const commit = useCallback((next: PendingCartEntry[]) => {
		if (next === entriesRef.current) return;
		entriesRef.current = next;
		setPendingLines(next);
	}, []);

	const releaseMutation = useCallback(() => {
		mutationRef.current = false;
		expectedRef.current = null;
		unresolvedRef.current = null;
		retryInFlightRef.current = false;
		setUnresolvedMutation(null);
		setMutationLocked(false);
	}, []);
	const acquireMutation = useCallback(() => {
		if (mutationRef.current || entriesRef.current.some((entry) => entry.status !== "failed")) return false;
		mutationRef.current = true;
		setMutationLocked(true);
		return true;
	}, []);
	const reconcileMutation = useCallback(() => {
		const expected = expectedRef.current;
		if (!expected || !snapshotRef.current) return;
		const line = snapshotRef.current.find((entry) => entry.id === expected.lineId);
		if (expected.quantity === null ? !line : line?.quantity === expected.quantity) releaseMutation();
	}, [releaseMutation]);
	const awaitMutationSnapshot = useCallback(
		(expected: MutationExpectation) => {
			expectedRef.current = expected;
			unresolvedRef.current = null;
			retryInFlightRef.current = false;
			setUnresolvedMutation(null);
			reconcileMutation();
		},
		[reconcileMutation],
	);
	const retainUnknownMutation = useCallback(
		(expected: MutationExpectation) => {
			mutationRef.current = true;
			setMutationLocked(true);
			expectedRef.current = expected;
			unresolvedRef.current = expected;
			retryInFlightRef.current = false;
			setUnresolvedMutation(expected);
			// Only the exact requested state establishes the result. An unchanged read cannot
			// prove rejection: the original write may still be running on the server.
			reconcileMutation();
		},
		[reconcileMutation],
	);
	const retryUnknownMutation = useCallback((expected: MutationExpectation) => {
		const unresolved = unresolvedRef.current;
		if (
			!unresolved ||
			retryInFlightRef.current ||
			upgradeRef.current ||
			unresolved.lineId !== expected.lineId ||
			unresolved.quantity !== expected.quantity
		)
			return false;
		retryInFlightRef.current = true;
		// A read from the first attempt must not release the shared lock mid-retry.
		expectedRef.current = null;
		return true;
	}, []);
	const syncMutationSnapshot = useCallback(
		(lines: CartSnapshotLine[]) => {
			snapshotRef.current = lines;
			reconcileMutation();
		},
		[reconcileMutation],
	);
	const readMutationSnapshot = useCallback(() => snapshotRef.current, []);
	const setUpgradePending = useCallback((pending: boolean) => {
		upgradeRef.current = pending;
		commitUpgradePending(pending);
	}, []);
	const isMutationBlocked = useCallback(() => mutationRef.current || upgradeRef.current, []);

	/** Analytics only: never read for any cart decision. */
	const openTriggerRef = useRef<CartOpenTrigger>("other");
	const openCart = useCallback((trigger?: CartOpenTrigger | SyntheticEvent) => {
		openTriggerRef.current =
			typeof trigger === "string" && (CART_OPEN_TRIGGERS as readonly string[]).includes(trigger)
				? trigger
				: "other";
		setIsOpen(true);
	}, []);
	const consumeOpenTrigger = useCallback(() => {
		const trigger = openTriggerRef.current;
		openTriggerRef.current = "other";
		return trigger;
	}, []);
	const closeCart = useCallback(() => {
		// An openCart while already open never reached a rising edge; do not let its
		// trigger describe the next opening.
		openTriggerRef.current = "other";
		setIsOpen(false);
		// A failed row is a prompt ("retry?"), not cart state: closing dismisses it,
		// exactly like the toast it accompanies. Pending/confirmed rows survive.
		for (const entry of entriesRef.current) {
			if (entry.status === "failed") dismissedRef.current.add(entry.attemptId);
		}
		commit(dropFailedPendingEntries(entriesRef.current));
	}, [commit]);
	const toggleCart = useCallback(() => {
		openTriggerRef.current = "other";
		setIsOpen((prev) => !prev);
	}, []);

	const startPendingCartLine = useCallback(
		(request: AddToCartRequest, attemptId: string) => {
			const entries = entriesRef.current;
			commit(
				appendPendingEntry(
					entries,
					createPendingEntry(request, attemptId, entries, authoritativeRef.current),
				),
			);
		},
		[commit],
	);

	const replacePendingCartLine = useCallback<CartContextType["replacePendingCartLine"]>(
		(previousAttemptId, request, attemptId) => {
			const entries = entriesRef.current;
			const target = entries.find((entry) => entry.attemptId === previousAttemptId);

			if (target) {
				// Only a FAILED row can be retried. A pending row means another click got
				// there first; a confirmed one is already on its way into the cart.
				if (target.status !== "failed") return "blocked";
				const others = entries.filter((entry) => entry.attemptId !== previousAttemptId);
				commit(
					replacePendingEntry(
						entries,
						previousAttemptId,
						// Same row, next attempt: the chain identity (and with it the rendered
						// DOM node) has to survive, otherwise the browser stops recognising a
						// double-click as one gesture.
						createPendingEntry(request, attemptId, others, authoritativeRef.current, {
							chainId: target.chainId,
							hasFailed: true,
						}),
					),
				);
				return "replaced";
			}

			// Row gone because the drawer was closed: the toast retry is still legitimate,
			// and consuming the id keeps a second click on the same toast blocked.
			if (dismissedRef.current.delete(previousAttemptId)) {
				commit(
					appendPendingEntry(
						entries,
						createPendingEntry(request, attemptId, entries, authoritativeRef.current),
					),
				);
				return "restarted";
			}

			return "blocked";
		},
		[commit],
	);

	const settlePendingCartLine = useCallback(
		(attemptId: string, settlement: PendingCartLineSettlement) => {
			commit(settlePendingEntry(entriesRef.current, attemptId, settlement));
		},
		[commit],
	);

	/**
	 * On a cold visit the drawer's `checkoutId` prop is null until `router.refresh()`
	 * lands, so the FIRST add rendered a checkout button pointing nowhere. The add
	 * response already names the checkout; remembering it lets the button work
	 * immediately. Never cleared on its own: a checkout id does not become invalid
	 * because props arrived, and the props value takes precedence at the call site.
	 *
	 * The CHANNEL rides along because a checkout belongs to exactly one, and the channel
	 * switcher navigates with `router.push` — which does NOT remount the `[channel]`
	 * layout that owns this provider. Without the pairing, an id learned on `eu` could
	 * outlive the switch and send a `us` visitor to a checkout in the wrong currency with
	 * the wrong contents.
	 */
	const [knownCheckout, setKnownCheckout] = useState<{ channel: string; checkoutId: string } | null>(null);
	const noteCheckoutId = useCallback((channel: string, checkoutId: string) => {
		if (channel && checkoutId) setKnownCheckout({ channel, checkoutId });
	}, []);

	const syncAuthoritativeCartLines = useCallback(
		(lines: AuthoritativeCartLine[]) => {
			authoritativeRef.current = lines;
			commit(reconcilePendingEntries(entriesRef.current, lines));
		},
		[commit],
	);

	const value = useMemo<CartContextType>(
		() => ({
			mutationLocked,
			upgradePending,
			acquireMutation,
			releaseMutation,
			awaitMutationSnapshot,
			unresolvedMutation,
			retainUnknownMutation,
			retryUnknownMutation,
			syncMutationSnapshot,
			readMutationSnapshot,
			setUpgradePending,
			isMutationBlocked,
			isOpen,
			openCart,
			closeCart,
			toggleCart,
			consumeOpenTrigger,
			pendingLines,
			startPendingCartLine,
			replacePendingCartLine,
			settlePendingCartLine,
			syncAuthoritativeCartLines,
			noteCheckoutId,
			knownCheckout,
		}),
		[
			mutationLocked,
			upgradePending,
			acquireMutation,
			releaseMutation,
			awaitMutationSnapshot,
			unresolvedMutation,
			retainUnknownMutation,
			retryUnknownMutation,
			syncMutationSnapshot,
			readMutationSnapshot,
			setUpgradePending,
			isMutationBlocked,
			isOpen,
			openCart,
			closeCart,
			toggleCart,
			consumeOpenTrigger,
			pendingLines,
			startPendingCartLine,
			replacePendingCartLine,
			settlePendingCartLine,
			syncAuthoritativeCartLines,
			noteCheckoutId,
			knownCheckout,
		],
	);

	return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
	const context = useContext(CartContext);
	if (!context) {
		throw new Error("useCart must be used within a CartProvider");
	}
	return context;
}
